// frontend/src/askos/reader/FacultyResourceReaderPage.js
//
// Reader for a single Faculty Shared Resource (lecture notes, case study,
// slide deck, etc.) — reached from the "Faculty Shared Resources" list on
// MySubjectsPage, never mixed with Studio content (see ContentReaderPage.js).
// Same read-only/no-copy convention as ContentReaderPage.js, reusing its CSS.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api, { askosAuthHeader } from '../askosApi';
import { normalizeMathForMarkdown } from '../prefsUtils';
import BookmarkButton from '../BookmarkButton';
import './ContentReaderPage.css';

const MATERIAL_TYPE_LABELS = {
    lecture_notes: 'Lecture Notes',
    case_study: 'Case Study',
    slide_deck: 'Slide Deck',
    document: 'Document',
};

const block = (e) => e.preventDefault();

const FacultyResourceReaderPage = ({ slug }) => {
    const { projectId, materialId } = useParams();
    const navigate = useNavigate();
    const [resource, setResource] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get(`/tenant/${slug}/askos/projects/${projectId}/resources/${materialId}`, { headers: askosAuthHeader() })
            .then(res => setResource(res.data))
            .catch(err => setError(err.response?.data?.detail || 'Could not load this resource.'));
    }, [slug, projectId, materialId]);

    if (error) {
        return (
            <div className="subpage">
                <button type="button" className="secondary-btn" onClick={() => navigate(-1)}>← Back</button>
                <p className="empty-note" style={{ marginTop: 16 }}>{error}</p>
            </div>
        );
    }

    if (!resource) {
        return <div className="subpage"><p className="empty-note">Loading…</p></div>;
    }

    return (
        <div className="reader-shell" onContextMenu={block} onCopy={block} onDragStart={block}>
            <header className="reader-header">
                <button type="button" className="secondary-btn" onClick={() => navigate(-1)}>← Back to Resources</button>
            </header>

            <div className="reader-content select-none">
                <article className="reader-item">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                        <div>
                            {resource.material_type && (
                                <span className="reader-item-badge">{MATERIAL_TYPE_LABELS[resource.material_type] || resource.material_type}</span>
                            )}
                            <h3 style={{ marginTop: 2 }}>{resource.title}</h3>
                        </div>
                        <BookmarkButton slug={slug} target={{ teaching_material_id: resource.id }} note={resource.title} />
                    </div>
                    <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {normalizeMathForMarkdown(resource.body)}
                    </ReactMarkdown>
                </article>
            </div>
        </div>
    );
};

export default FacultyResourceReaderPage;
